import {
  FC, useCallback, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import moment, { Moment } from 'moment-timezone';
import { BaseButtonMenu, TListBaseItem } from '@mgm/common/components/base';
import Icon from '@mdi/react';
import { mdiChevronDown } from '@mdi/js';
import Box from '@mui/material/Box';
import { sleep } from '@mgm/common/utils';
import { TBaseTable } from '@mgm/common/components/table';
import { CustomToolbar } from '../../../../components/table';
import { useGetOrderFirstPageAPI, useGetOrdersMappingAPI, useOrders } from '../hooks';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';
import { ordersActions } from '../../../../store/orders/slice';
import { useGetSheetsAPI } from '../../../../hooks/api/useSheets';
import { useGetOrdersCountAPI } from '../../../../hooks/api/useOrders';
import { getMenuOptionName, getOrdersChosenDates } from '../../../../store/orders/selectors';
import { getAuthenticateUser } from '../../../../store/authenticate/selectors';
import { commissionsActions } from '../../../../store/commissions/slice';
import { getTableCommissionView } from '../../../../store/commissions/selectors';

const OrdersTableHeader: FC<TBaseTable & {
  isDisplayFilter?: boolean,
  setIsFetchByDate: (val: boolean) => void
  isCommissionView?: boolean
  isDisplaySelectedMembers?: boolean
}> = ({
  apiRef,
  printRef,
  handleScrollTable,
  isDisplayFilter = true,
  setIsFetchByDate,
  isFullTable = false,
  setIsFullTable = () => {},
  handleExport,
  isCommissionView,
  isDisplaySelectedMembers = false,
}) => {
  const { t } = useTranslation(['common', 'dashboard', 'schedule', 'commissions']);
  const dispatch = useAppDispatch();
  const { shop } = useAppSelector(getAuthenticateUser);
  const [calculateByName, setCalculateByName] = useState(t('calculateBy', { ns: 'commissions' }));
  const [menuValue, setMenuValue] = useState('last7Days');
  const [isOpenCustomPicker, setIsOpenCustomPicker] = useState(false);
  const tableCommissionView = useAppSelector(getTableCommissionView);
  const menuOptionName = useAppSelector(getMenuOptionName);

  const ordersChosenDates = useAppSelector(getOrdersChosenDates);

  const {
    ordersSetDates,
    isBtnDisabled,
    setIsBtnDisabled,
  } = useOrders();

  const { refetch: refetchMappings } = useGetOrdersMappingAPI({
    isMainOrdersPage: false,
    dates: ordersChosenDates,
  });

  const { isFetching, refetch } = useGetOrderFirstPageAPI({
    isMainOrdersPage: false,
    dates: ordersChosenDates,
  });

  const { refetch: referchSheets } = useGetSheetsAPI({
    isMainSheetsPage: false,
    dates: ordersChosenDates,
    timezone: shop.shopTimezone,
  });

  const { refetch: refetchCount } = useGetOrdersCountAPI({
    isMainPage: false,
    dates: ordersChosenDates,
  });

  const onDatesChanged = (dates: [Moment, Moment]) => {
    const [start, end] = dates;

    const formatted = `${start?.format('MMM D')} - ${end?.format('MMM D, YYYY')}`;
    dispatch(ordersActions.setMenuOptionName(formatted));

    ordersSetDates(dates);
  };

  const getOrdersByDates = useCallback(async () => {
    dispatch(ordersActions.setIsOrdersLoading(true));
    setIsFetchByDate(true);
    await sleep(1000);
    await referchSheets();
    await refetchCount();
    await refetchMappings();
    await refetch();
    setIsBtnDisabled(true);
  }, [ordersChosenDates]);

  const items = useMemo(() => {
    const timezone = shop.shopTimezone;

    const dateRangePresets = {
      yesterday: [
        moment.tz(timezone).subtract(1, 'day').startOf('day'),
        moment.tz(timezone).subtract(1, 'day').endOf('day'),
      ],
      today: [
        moment.tz(timezone).startOf('day'),
        moment.tz(timezone).endOf('day'),
      ],
      last7Days: [
        moment.tz(timezone).subtract(6, 'days').startOf('day'),
        moment.tz(timezone).endOf('day'),
      ],
      last14Days: [
        moment.tz(timezone).subtract(13, 'days').startOf('day'),
        moment.tz(timezone).endOf('day'),
      ],
      last30Days: [
        moment.tz(timezone).subtract(29, 'days').startOf('day'),
        moment.tz(timezone).endOf('day'),
      ],
      thisMonth: [
        moment.tz(timezone).startOf('month'),
        moment.tz(timezone).endOf('month'),
      ],
    };

    const createItem = (name: string, value: string) => ({
      name,
      value,
      isSelected: menuValue === value,
      action: async () => {
        dispatch(ordersActions.setMenuOptionName(name));

        setMenuValue(value);

        if (value !== 'custom') {
          dispatch(ordersActions.setOrdersChosenDates(dateRangePresets[value as keyof typeof dateRangePresets] as [Moment, Moment]));
          await getOrdersByDates();
        }
      },
    });

    return [
      createItem(t('yesterday', { ns: 'dashboard' }), 'yesterday'),
      createItem(t('today', { ns: 'schedule' }), 'today'),
      createItem(t('lastSevenDays', { ns: 'dashboard' }), 'last7Days'),
      createItem(t('last14Days', { ns: 'dashboard' }), 'last14Days'),
      createItem(t('last30Days', { ns: 'dashboard' }), 'last30Days'),
      createItem(t('thisMonth', { ns: 'dashboard' }), 'thisMonth'),
      {
        name: t('customFixedDateRange', { ns: 'dashboard' }),
        value: 'custom',
        isSelected: menuValue === 'custom',
        action: () => {
          setMenuValue('custom');
          setIsOpenCustomPicker(true);
        },
      },
    ];
  }, [menuValue, shop.shopTimezone, getOrdersByDates]);

  const calculateItems = useMemo(() => [
    {
      name: t('calculateEntirePeriod', { ns: 'commissions' }),
      value: 0,
      isSelected: tableCommissionView === 0,
      action: () => {
        setCalculateByName(t('calculateEntirePeriod', { ns: 'commissions' }));
        dispatch(commissionsActions.setTableCommissionView(0));
      },
    },
    {
      name: t('calculateDailyCommission', { ns: 'commissions' }),
      value: 1,
      isSelected: tableCommissionView === 1,
      action: () => {
        setCalculateByName(t('calculateDailyCommission', { ns: 'commissions' }));
        dispatch(commissionsActions.setTableCommissionView(1));
      },
    },
  ], [calculateByName, tableCommissionView]);

  return (
    <CustomToolbar
      isDisplayCustomFilter={isDisplayFilter}
      handleScrollTable={handleScrollTable}
      printRef={printRef}
      apiRef={apiRef}
      isDisplayPicker
      chosenDates={ordersChosenDates}
      isPickerBtnDisable={isBtnDisabled}
      handleSetDates={onDatesChanged}
      onPickerSubmit={getOrdersByDates}
      isPickerLoading={isFetching}
      pickerButtonText={t('load')}
      isOrders
      isFullTable={isFullTable}
      setFullTable={setIsFullTable}
      values={{
        dateRange: ordersChosenDates,
      }}
      handleExport={handleExport}
      pickerItems={items as Array<TListBaseItem>}
      selectedPickerName={menuOptionName}
      isOpenCustomPicker={isOpenCustomPicker}
      setIsOpenCustomPicker={setIsOpenCustomPicker}
      isDisplaySelectedMembers={isDisplaySelectedMembers}
      {...(isCommissionView && {
        customAction: (
          <Box pr={1}>
            <BaseButtonMenu
              isRemoveBtnHover={false}
              buttonProps={{
                children: calculateByName,
                endIcon: <Icon path={mdiChevronDown} size={0.8} />,
                size: 'small',
                variant: 'contained',
              }}
              listProps={{
                items: calculateItems as unknown as Array<TListBaseItem>,
                sx: { width: 'auto' },
                itemfontsize: 12,
              }}
            />
          </Box>
        ),
      })}
    />
  );
};

export {
  OrdersTableHeader,
};
